import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { HiAcademicCap, HiChevronLeft, HiChevronRight } from "react-icons/hi";
import { useCertifications } from "../../hooks/useCertifications";
import CertificationCard from "../ui/CertificationCard";
import CertificationModal from "../ui/CertificationModal";

const Certifications = () => {
    const { certifications, loading, error } = useCertifications();
    const [selectedCert, setSelectedCert] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [itemsPerView, setItemsPerView] = useState(5);
    const [isTransitioning, setIsTransitioning] = useState(false);

    // Calcular items por vista según el ancho de pantalla
    useEffect(() => {
        const handleResize = () => {
            if (window.innerWidth < 640) {
                setItemsPerView(2); // 2 items en móvil
            } else if (window.innerWidth < 768) {
                setItemsPerView(2);
            } else if (window.innerWidth < 1024) {
                setItemsPerView(3);
            } else if (window.innerWidth < 1280) {
                setItemsPerView(4);
            } else {
                setItemsPerView(5);
            }
        };

        handleResize();
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    // Resetear índice si cambia el número de certificaciones
    useEffect(() => {
        if (certifications && currentIndex > Math.max(0, certifications.length - itemsPerView)) {
            setCurrentIndex(Math.max(0, certifications.length - itemsPerView));
        }
    }, [certifications, itemsPerView, currentIndex]);

    const handleCertClick = (cert) => {
        setSelectedCert(cert);
        setShowModal(true);
    };

    const handlePrevious = () => {
        if (isTransitioning) return;
        setIsTransitioning(true);
        setCurrentIndex((prev) => Math.max(0, prev - itemsPerView));
        setTimeout(() => setIsTransitioning(false), 500);
    };

    const handleNext = () => {
        if (isTransitioning) return;
        if (certifications) {
            setIsTransitioning(true);
            setCurrentIndex((prev) => Math.min(certifications.length - itemsPerView, prev + itemsPerView));
            setTimeout(() => setIsTransitioning(false), 500);
        }
    };

    // Determinar si mostrar las flechas
    const canGoLeft = currentIndex > 0;
    const canGoRight = certifications && currentIndex < certifications.length - itemsPerView;

    // Calcular número real de páginas necesarias
    const totalPages = certifications ? Math.ceil(certifications.length / itemsPerView) : 0;

    // Calcular la página actual basándose en el índice actual
    const currentPage = certifications ? (() => {
        // Si estamos en la última página o más allá
        if (currentIndex >= certifications.length - itemsPerView) {
            return totalPages - 1;
        }

        // Para páginas intermedias, encontrar qué página contiene el currentIndex
        // Dividir y redondear al entero más cercano da mejor resultado
        return Math.round(currentIndex / itemsPerView);
    })() : 0;

    if (loading) {
        return (
            <section id="certificaciones" className="mt-32">
                <div className="flex items-center mb-8">
                    <div className="mr-4 w-8 h-8 rounded-full border-2 border-white dark:border-gray-800 bg-white dark:bg-gray-800 flex items-center justify-center">
                        <HiAcademicCap className="size-5" />
                    </div>
                    <h2 className="text-3xl font-bold">Certificaciones</h2>
                </div>
                <div className="flex justify-center items-center py-12">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
                </div>
            </section>
        );
    }

    if (error) {
        return (
            <section id="certificaciones" className="mt-32">
                <div className="flex items-center mb-8">
                    <div className="mr-4 w-8 h-8 rounded-full border-2 border-white dark:border-gray-800 bg-white dark:bg-gray-800 flex items-center justify-center">
                        <HiAcademicCap className="size-5" />
                    </div>
                    <h2 className="text-3xl font-bold">Certificaciones</h2>
                </div>
                <p className="text-center text-red-500 dark:text-red-400">Error al cargar las certificaciones</p>
            </section>
        );
    }

    if (!certifications || certifications.length === 0) {
        return null;
    }

    return (
        <section id="certificaciones" className="mt-32">
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
                className="flex items-center mb-8"
            >
                <div className="mr-4 w-8 h-8 rounded-full border-2 border-white dark:border-gray-800 bg-white dark:bg-gray-800 flex items-center justify-center">
                    <HiAcademicCap className="size-5" />
                </div>
                <h2 className="text-3xl font-bold">Certificaciones</h2>
            </motion.div>

            <div className="relative">
                {/* Contenedor del slider */}
                <div className="overflow-hidden py-4 sm:py-6">
                    <div
                        className="flex transition-transform duration-500 ease-in-out"
                        style={{
                            transform: `translateX(-${currentIndex * (100 / itemsPerView)}%)`,
                        }}
                    >
                        {certifications.map((cert) => (
                            <div
                                key={cert.id}
                                className="flex-shrink-0 flex justify-center px-2 sm:px-3"
                                style={{
                                    width: `${100 / itemsPerView}%`,
                                }}
                            >
                                <CertificationCard certification={cert} onClick={() => handleCertClick(cert)} />
                            </div>
                        ))}
                    </div>
                </div>

                {/* Controles de navegación - botones + indicadores */}
                {certifications.length > itemsPerView && (
                    <div className="flex items-center justify-center gap-4">
                        {/* Left arrow */}
                        <motion.button
                            onClick={handlePrevious}
                            disabled={!canGoLeft}
                            className={`flex-shrink-0 p-1.5 sm:p-2 rounded-full bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 shadow-lg transition-all ${
                                canGoLeft
                                    ? "hover:bg-gray-50 dark:hover:bg-gray-700 hover:scale-110 cursor-pointer"
                                    : "opacity-30 cursor-auto"
                            }`}
                            aria-label="Ver certificaciones anteriores"
                            animate={canGoLeft ? {
                                x: [-2, 0, -2],
                            } : {}}
                            transition={{
                                duration: 2,
                                repeat: Infinity,
                                ease: "easeInOut",
                            }}
                            whileHover={canGoLeft ? {
                                x: 0,
                                transition: { duration: 0 },
                            } : {}}
                        >
                            <HiChevronLeft className="w-4 h-4 sm:w-5 sm:h-5 text-gray-700 dark:text-gray-300" />
                        </motion.button>

                        {/* Indicadores de posición */}
                        <div className="flex justify-center gap-2">
                            {Array.from({ length: totalPages }).map((_, pageIndex) => {
                                const isActive = currentPage === pageIndex;

                                return (
                                    <button
                                        key={pageIndex}
                                        onClick={() => {
                                            if (!isTransitioning) {
                                                // Calcular el índice de inicio de la página
                                                const pageStart = pageIndex * itemsPerView;
                                                // Asegurar que no excedemos el límite
                                                const newIndex = Math.min(pageStart, certifications.length - itemsPerView);
                                                setCurrentIndex(Math.max(0, newIndex));
                                            }
                                        }}
                                        className={`h-2 rounded-full transition-all ${
                                            isActive
                                                ? "w-8 bg-blue-500 dark:bg-blue-400"
                                                : "w-2 bg-gray-300 dark:bg-gray-600 hover:bg-gray-400 dark:hover:bg-gray-500"
                                        }`}
                                        aria-label={`Ir a la página ${pageIndex + 1} de certificaciones`}
                                    />
                                );
                            })}
                        </div>

                        {/* Right arrow */}
                        <motion.button
                            onClick={handleNext}
                            disabled={!canGoRight}
                            className={`flex-shrink-0 p-1.5 sm:p-2 rounded-full bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 shadow-lg transition-all ${
                                canGoRight
                                    ? "hover:bg-gray-50 dark:hover:bg-gray-700 hover:scale-110 cursor-pointer"
                                    : "opacity-30 cursor-auto"
                            }`}
                            aria-label="Ver más certificaciones"
                            animate={canGoRight ? {
                                x: [2, 0, 2],
                            } : {}}
                            transition={{
                                duration: 2,
                                repeat: Infinity,
                                ease: "easeInOut",
                            }}
                            whileHover={canGoRight ? {
                                x: 0,
                                transition: { duration: 0 },
                            } : {}}
                        >
                            <HiChevronRight className="w-4 h-4 sm:w-5 sm:h-5 text-gray-700 dark:text-gray-300" />
                        </motion.button>
                    </div>
                )}
            </div>

            {/* Modal */}
            {showModal && selectedCert && <CertificationModal certification={selectedCert} onClose={() => setShowModal(false)} />}
        </section>
    );
};

export default Certifications;
